// eslint-disable-next-line no-restricted-imports
import { OrderDetailsDto, OrderFragment } from 'api/generated/graphql';
import { parseFileEntity } from 'api/requests/onboarding/helpers';
import {
  OrderExecutionTermsOperationKind,
  OrderExecutionTermsOrderType,
  OrderMarketType,
  OrderOperationType,
  OrderTransactionVenueType,
} from 'api/types/entity/account/order';
import { OrderDetailsEntity } from 'api/types/entity/account/order/orderDetailsEntity';
import { OrderEntity } from 'api/types/entity/account/order/orderEntity';
import { OrderStatusType } from 'api/types/entity/account/order/orderStatusType';
import { lodash } from 'helpers';

const parseDetails = (details: OrderDetailsDto): OrderDetailsEntity => ({
  accountNumber: details.accountNumber,
  accountOwnerName: details.accountOwnerName ?? undefined,
  brokerName: {
    value: details.brokerName.value,
    referenceRecordId: details.brokerName.referenceRecordId ?? undefined,
  },
  custodianOrganization: {
    value: details.custodianOrganization.value,
    referenceRecordId:
      details.custodianOrganization.referenceRecordId ?? undefined,
  },
  custodianOrganizationCode: details.custodianOrganizationCode ?? undefined,
});

export const parseOrderEntity = (data: OrderFragment): OrderEntity => ({
  id: data.id,
  status: OrderStatusType[data.status],
  number: data.number ?? undefined,
  operationType: OrderOperationType[data.operationType],
  versionNumber: data.version?.number,
  createdAt: new Date(data.createdAt),

  signedAt: data.signedAt ? new Date(data.signedAt) : undefined,

  startDate: data.startDate ? new Date(data.startDate) : undefined,
  endDate: new Date(data.endDate),
  closedDate: data.closedDate ? new Date(data.closedDate) : undefined,

  executionPeriodInBusinessDays:
    data.executionPeriodInBusinessDays ?? undefined,

  transactionVolume: data.transactionVolume
    ? {
        quantity: data.transactionVolume.quantity,
        unitPrice: data.transactionVolume.unitPrice ?? undefined,
        transactionAmount:
          data.transactionVolume.transactionAmount ?? undefined,
        currency: data.transactionVolume.currency,
      }
    : undefined,

  transferVolume: data.transferVolume
    ? {
        quantity: data.transferVolume.quantity,
        unitPrice: data.transferVolume.unitPrice ?? undefined,
        transactionAmount: data.transferVolume.transactionAmount ?? undefined,
        currency: data.transferVolume.currency ?? undefined,
      }
    : undefined,

  financialInstrument: {
    nominalValue: data.financialInstrument.nominalValue,
    isin: {
      value: data.financialInstrument.isin.value,
      referenceRecordId:
        data.financialInstrument.isin.referenceRecordId ?? undefined,
    },
    securityType: data.financialInstrument.securityType ?? undefined,
    issuer: data.financialInstrument.issuer ?? undefined,
  },

  executionTerms: {
    additionalNotes: data.executionTerms.additionalNotes ?? undefined,
    orderType: data.executionTerms.orderType
      ? OrderExecutionTermsOrderType[data.executionTerms.orderType]
      : undefined,
    yield: data.executionTerms.yield ?? undefined,
    operationKind: data.executionTerms.operationKind
      ? OrderExecutionTermsOperationKind[data.executionTerms.operationKind]
      : undefined,
    operationPurpose: data.executionTerms.operationPurpose ?? undefined,
    marketType: data.marketType ? OrderMarketType[data.marketType] : undefined,
    transactionVenue: data.transactionVenue
      ? OrderTransactionVenueType[data.transactionVenue]
      : undefined,
  },

  senderDetails: data.senderDetails
    ? parseDetails(data.senderDetails)
    : undefined,

  recipientDetails: data.recipientDetails
    ? parseDetails(data.recipientDetails)
    : undefined,

  additionalParameters: data.additionalParameters
    ? {
        settlementDate: data.additionalParameters.settlementDate
          ? new Date(data.additionalParameters.settlementDate)
          : undefined,
        transactionDate: data.additionalParameters.transactionDate
          ? new Date(data.additionalParameters.transactionDate)
          : undefined,
      }
    : undefined,

  confirmationDocument: data.confirmationDocument
    ? {
        contractDate: data.confirmationDocument.contractDate
          ? new Date(data.confirmationDocument.contractDate)
          : undefined,
        contractNumber: data.confirmationDocument.contractNumber ?? undefined,
        files: lodash
          .compact(data.confirmationDocument.files)
          .map(parseFileEntity),
      }
    : undefined,

  operations: lodash.compact(data.operations).map((operation) => ({
    id: operation.id,
    executedAt: new Date(operation.executedAt),
    settlementDate: new Date(operation.settlementDate),
    isin: data.financialInstrument.isin.value,
    quantity: operation.quantity,
    currency: data.transactionVolume?.currency,
    executionPlace: operation.executionPlace
      ? {
          id: operation.executionPlace.id,
          name: operation.executionPlace.name ?? undefined,
        }
      : undefined,
    netUnitPrice: operation.netUnitPrice,
    volume: operation.volume ?? undefined,
  })),
});
